/**
 * `Sheet`: the frame a dialog's content sits in, a library member (COMPONENT_LIBRARY.md §4).
 * Promoted from My Orders' `frames/Sheet` in L0 (2026-10-06, the user: "meet the rules, look
 * same"), which is now a thin wrapper over this one passing My Orders' own values.
 *
 * The PROPS and the header are shared; the container is per platform, because the platforms'
 * UI genuinely differs (rule 9's one exception): a centred dialog in a transparent `Modal` on web
 * (`index.desktop.tsx`), a bottom sheet on a phone (`index.native.tsx`).
 *
 * ⚠️ A sheet STAYS MOUNTED and is toggled by `visible`: both containers animate out, and neither
 * can animate a component its parent has just unmounted. So a sheet's content re-initialises its
 * own fields in an effect on `visible`, not on a fresh mount.
 */
import React from "react";
import type { DimensionValue } from "react-native";
import { Text, View } from "react-native";

import { RADIUS, SHADOWS } from "@rista/ui/library/theme/tokens";
import type { Theme } from "@rista/ui/library/theme/theme";
import { lookFor, type SlotStyle } from "@rista/ui/library/theme/variants";
import { sheetLegacyLook } from "./legacyLook";
import { sheetLook } from "./look";

export interface SheetProps {
  visible: boolean;
  onClose: () => void;
  /** What a screen reader says for the backdrop that closes it: the host's words (rule: words passed in). */
  closeLabel: string;
  title?: string;
  subtitle?: string;
  /**
   * WEB only: a ✕ that closes it, labelled `closeLabel`, floating OUTSIDE the dialog, above its top-end
   * corner, on the scrim. A phone's bottom sheet draws none (the user, 2026-10-21). Off by default: the
   * backdrop (and Escape, or a pan down) already close every sheet.
   */
  showClose?: boolean;
  /** On the dialog; the backdrop gets `<testID>-backdrop`, the ✕ `<testID>-close`. */
  testID?: string;
  /** `alertdialog` for a warning that interrupts; `dialog` otherwise. */
  role?: "dialog" | "alertdialog";
  /**
   * On web: `wide` (half the window, at most 720: My Orders' sheets), `narrow` (at most 500: a warning)
   * or `large` (80 % of the window both ways: web v1's customise popup, New Order v2's customiser).
   */
  size?: SheetSize;
  /**
   * On a phone: `portal` (the default) draws a `@gorhom/bottom-sheet` modal through the provider the
   * app mounts at its root; `modal` draws the bottom sheet inside a React Native `Modal`, above every
   * other modal, for content that must show whatever is open (v2's alert). Web ignores it.
   */
  presentation?: "portal" | "modal";
  /**
   * On a phone, `portal` only: gorhom renders the body in its provider's portal, outside any
   * context between that provider and the sheet, so a host wraps the body to provide it again.
   */
  wrapContent?: (body: React.ReactNode) => React.ReactNode;
  /**
   * The header's and the phone handle's look: `legacy` (the default), as the live My Orders sheets draw
   * them; `pos`, the design system's roles, for v2 (DESIGN_SYSTEM.md §7.2, QD5). The frame is the same.
   */
  appearance?: SheetAppearance;
  children: React.ReactNode;
}

export type SheetAppearance = "legacy" | "pos";

/** the frame's styles, the same for every caller, and the chrome's for an appearance */
export function sheetStyles(theme: Theme, appearance: SheetAppearance = "legacy") {
  const frame = lookFor(sheetLook, theme);
  const chrome: Record<"header" | "title" | "subtitle" | "handle", SlotStyle> =
    appearance === "pos" ? frame : lookFor(sheetLegacyLook, theme);
  return { frame, chrome };
}

export type SheetSize = "wide" | "narrow" | "large";

/** The web dialog's box per size: `wide` is My Orders' sheet as it was; `narrow` web v1's warning modal (MUI). */
export const DIALOG_SIZES: Record<SheetSize, { width: DimensionValue; height?: DimensionValue; maxWidth: number; borderRadius: number; boxShadow?: string }> = {
  wide: { width: "50%", maxWidth: 720, borderRadius: RADIUS.xl },
  large: { width: "80%", height: "80%", maxWidth: 1600, borderRadius: 0 },
  narrow: { width: "92%", maxWidth: 500, borderRadius: RADIUS.lg, boxShadow: SHADOWS.dialog },
};

/** `<testID>-backdrop`, or nothing */
export const backdropTestID = (testID?: string) => (testID ? `${testID}-backdrop` : undefined);

/** `<testID>-close` (the `showClose` ✕), or nothing */
export const closeTestID = (testID?: string) => (testID ? `${testID}-close` : undefined);

export function SheetHeader({ title, subtitle, chrome }: Pick<SheetProps, "title" | "subtitle"> & { chrome: ReturnType<typeof sheetStyles>["chrome"] }) {
  if (!title && !subtitle) return null;
  const styles = chrome;
  return (
    <View style={styles.header}>
      {!!title && (
        <Text role="heading" style={styles.title}>
          {title}
        </Text>
      )}
      {!!subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
    </View>
  );
}
